import './MMM.scss';
import * as R from 'ramda';
import {
  ComponentProps,
  ReactElement,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
  ReactNode,
  JSX
} from 'react';
import { MdEdit } from 'react-icons/md';
import { useQueryClient } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { Navigate, Route, useLocation as useRouterLocation } from 'react-router-dom';
import { Routes } from '@datadog/browser-rum-react/react-router-v6';
import { Button } from '@Components/Button';
import { Checkbox } from '@Components/FormCheck';
import { Page } from '@Components/Page/Page';
import { Appearance, Design, Size } from '@Components/designConstants';
import { MmmRunSelector } from '@Pages/MMM/MmmRunSelector/MmmRunSelector';
import { ModelRun, runKey } from '@Pages/MMM/MmmRunSelector/mmm-run-selector-constants';
import { TINUITI_TEST_HOME_SERVICES, TINUITI_TEST_HOME_SERVICES_PROXY } from '@Pages/MMM/mmm-utils';
import { useSetAreYouSure, useSetError } from '@src/redux/modals';
import { fullNameSelector, isInternalSelector } from '@src/redux/user';
import { useCompanyInfo } from '@src/utils/hooks/useCompanyInfo';
import { useTabbedNav } from '@src/utils/hooks/useNav';
import { ScenarioPlanning } from '../GeoMMM/ScenarioPlanning/ScenarioPlanning';
import { GROUP_BY_ENUM, MODEL_TYPES } from '../GeoMMM/ScenarioPlanning/ScenarioPlanningUtils';
import { useScenarioPlanningToolBar } from '../GeoMMM/ScenarioPlanning/UseScenarioPlanningToolBar';
import { Optimizations } from './Optimizations';
import { FAQ as FAQPage } from '../MMM/FAQ/FAQ';
import { OfflineInputs } from '../MMM/OfflineInputs/OfflineInputs';
import {
  getEfficientFrontierDefaultDates,
  TINUITITEST_CID,
  TINUITITEST_FEATURE_MAP_PROXY_CID
} from './constants';
import { ModelConfig, ModelMetadata } from './hroas-types';
import { EditModelWeightingModal } from './EditModelWeightingModal';
import { EfficientFrontier } from './EfficientFrontier';
import { ModelResults } from './ModelResults/ModelResults';
import { SecondaryNav } from './SecondaryNav';
import {
  calculateModelMape,
  calculateModelRefreshDate,
  formatMapeAsString,
  modelSetSignature,
  StackedCSVRow
} from './utils/hroasUtils';
import {
  fetchChannelMixData,
  fetchMarketingOutcomesData,
  getChannelMixQueryKey,
  getMarketingOutcomesQueryKey
} from './utils/useEfficientFrontierData';
import { fetchModelOverviewRows, useRunSlots } from './utils/useModelResultsData';
import { slotsToModelConfigs } from './utils/slots';
import {
  HROAS_MODEL_ID_TO_FEATURE_MAPPING_TYPE,
  useFeatureMapsByModelType
} from './utils/useFeatureMapping';
import { fetchHroasResults, getHroasResultsQueryKey } from './utils/hroasResults';
import {
  getHroasRunsQueryKey,
  HroasRunSummary,
  listHroasRuns,
  outcomeVariableToHroasKpi,
  hroasToModelRuns,
  pickDefaultRun,
  setHroasRunPublished
} from './utils/hroasRuns';

const createFormattedTooltip = (
  label: string,
  weight: number,
  modelId: string,
  setShowEditModal: () => void,
  modelMetadata: ModelMetadata,
  omitWeight: boolean
): ReactNode => (
  <div className='hroas-formatted-tooltip-content'>
    <div>
      <strong>{label}</strong>
    </div>
    {!omitWeight && (
      <div className='hroas-formatted-tooltip-edit-button'>
        <span>Model Weight: {weight.toFixed(2)}</span>
        <MdEdit
          className='hroas-formatted-tooltip-edit-icon'
          onClick={e => {
            e.stopPropagation();
            setShowEditModal();
          }}
        />
      </div>
    )}
    <div>Latest Refresh: {modelMetadata.runDate}</div>
    <div>OOS MAPE: {modelMetadata.mape}</div>
  </div>
);

const TabKey = {
  EFFICIENT_FRONTIER: 'efficient-frontier',
  MODEL_RESULTS: 'model-results',
  BUDGET_OPTIMIZATION: 'budget-optimizations',
  SCENARIO_PLANNING: 'scenario-planning',
  FAQ: 'FAQ',
  OFFLINE_INPUTS: 'offline-inputs'
} as const;
type TabKey = (typeof TabKey)[keyof typeof TabKey];

interface HroasPublishToggleProps {
  currentRun: HroasRunSummary;
  onConfirm: () => void;
}

// Internal-only "Client-Facing" publish toggle. Mirrors tinuiti-frontend's
// MmmClientFacingToggle but built inline here (bpm-frontend design constants).
// Writes the run-level `hroas_runs.published` flag via `on_hroas_run_published`.
const HroasPublishToggle = ({ currentRun, onConfirm }: HroasPublishToggleProps) => {
  const setAreYouSure = useSetAreYouSure();
  const isVisible = currentRun.published;
  return (
    <Button
      size={Size.LG}
      // Outlined + light + trailing check to match the neighboring outlined Run / Outcome
      // Variable / Group by controls (and the canonical MmmClientFacingToggle), instead of the
      // default filled look that read as oddly sized next to them.
      className='hroas-publish-toggle'
      data-testid='hroas-client-facing-toggle'
      design={Design.NEUTRAL}
      appearance={Appearance.OUTLINED}
      // Defensive: the picker already filters to succeeded runs, but never let a
      // non-succeeded run be published (the server rejects it too).
      disabled={currentRun.status !== 'succeeded'}
      trailingElement={<Checkbox checked={isVisible} readOnly />}
      onClick={() =>
        setAreYouSure({
          title: isVisible ? 'Hide run from clients?' : 'Show run to clients?',
          message: `You're about to make this run ${
            isVisible ? 'hidden from' : 'visible to'
          } clients. Are you sure you want to continue?`,
          cancelText: 'Cancel',
          okayText: 'Continue',
          onOkay: onConfirm
        })
      }
    >
      Client-Facing
    </Button>
  );
};

export const HROAS = () => {
  const setError = useSetError();
  const queryClient = useQueryClient();
  const { cid: originalCompany } = useCompanyInfo();

  let { cid: company } = useCompanyInfo();
  if (company === TINUITI_TEST_HOME_SERVICES) {
    company = TINUITI_TEST_HOME_SERVICES_PROXY;
  }

  const [groupByOptions, setGroupByOptions] = useState<{ label: string; value: string }[]>([]);
  const disabledPlatform = useMemo(() => {
    if (!R.isEmpty(groupByOptions)) {
      return groupByOptions.filter(option => option.value === 'platform').length === 0;
    }
    return false;
  }, [groupByOptions]);
  const [pageActions] = useState<JSX.Element | undefined>(undefined);
  const [runOptions, setRunOptions] = useState<HroasRunSummary[]>([]);
  const [currentRun, setCurrentRun] = useState<HroasRunSummary>();
  const isInternal = useSelector(isInternalSelector);
  const currentUserName: string = useSelector(fullNameSelector) ?? '';
  const [showActualizedData] = useState<boolean>(true);
  const [loading, setLoading] = useState(true);
  const [modelsData, setModelsData] = useState<ModelConfig[]>([]);
  const [showEditModal, setShowEditModal] = useState(false);
  const [stackedCSVData, setStackedCSVData] = useState<StackedCSVRow[]>([]);

  const NAVS = useMemo(() => {
    const baseNavs = [{ label: 'FAQ', key: TabKey.FAQ }];

    if (isInternal || company === TINUITITEST_CID) {
      return [
        { label: 'Efficient Frontier', key: TabKey.EFFICIENT_FRONTIER },
        { label: 'Model Results', key: TabKey.MODEL_RESULTS },
        ...baseNavs,
        { label: 'Budget Optimizations', key: TabKey.BUDGET_OPTIMIZATION },
        { label: 'Scenario Planning', key: TabKey.SCENARIO_PLANNING },
        { label: 'Offline Inputs', key: TabKey.OFFLINE_INPUTS }
      ];
    }

    return baseNavs;
    // eslint-disable-next-line react-hooks/preserve-manual-memoization -- NAVS depends only on company/isInternal; React Compiler cannot prove baseNavs isn't mutated later, but it isn't
  }, [company, isInternal]);

  const { tab, goToTab } = useTabbedNav({
    baseURL: 'hroas',
    defaultKey: TabKey.MODEL_RESULTS,
    navs: NAVS.map(nav => nav.key)
  });

  const [optimisticTab, setOptimisticTab] = useState(tab);
  useEffect(() => {
    setOptimisticTab(tab);
  }, [tab]);

  const handleNav = useCallback(
    (newTab: string) => {
      // Page.onNav hands back a raw string; useTabbedNav is parameterized to TabKey
      // (inferred from defaultKey/navs), and NAVS keys are all TabKey values.
      setOptimisticTab(newTab as TabKey);
      goToTab(newTab as TabKey);
    },
    [goToTab]
  );

  // TODO: Update to handle Efficient Frontier
  const {
    groupBy: toolbarGroupBy,
    setGroupBy: setToolbarGroupBy,
    outcomeVariable: toolbarOutcomeVariable,
    toolbar
  } = useScenarioPlanningToolBar({
    outcomeVariables: ['Revenue', 'KPI Volume'],
    hideOutcomeVariable: originalCompany === TINUITITEST_CID,
    showScenarioPlanningControls: false,
    showEfficientFrontierControls: false
  });

  // The Outcome Variable selection drives which runs + results we load:
  // "Revenue" → revenue, "KPI Volume" → non_revenue (defaults to revenue before
  // the toolbar initializes). This is derived AFTER the toolbar hook so the run
  // list, results prefetch, and default-run pick all key on the active kpi.
  const kpi = useMemo(
    () => outcomeVariableToHroasKpi(toolbarOutcomeVariable),
    [toolbarOutcomeVariable]
  );

  const groupByEnumToPropertyName = (enumValue: GROUP_BY_ENUM): string => {
    switch (enumValue) {
      case GROUP_BY_ENUM.CHANNEL:
        return 'channel';
      case GROUP_BY_ENUM.PLATFORM:
        return 'platform';
      case GROUP_BY_ENUM.TACTIC:
        return 'name';
      default:
        return 'channel';
    }
  };

  const groupBy = useMemo(() => groupByEnumToPropertyName(toolbarGroupBy), [toolbarGroupBy]);

  const setGroupBy = useCallback(
    (value: string) => {
      if (value === 'channel') {
        setToolbarGroupBy(GROUP_BY_ENUM.CHANNEL);
      } else if (value === 'platform') {
        setToolbarGroupBy(GROUP_BY_ENUM.PLATFORM);
      } else if (value === 'name') {
        setToolbarGroupBy(GROUP_BY_ENUM.TACTIC);
      } else {
        setToolbarGroupBy(value as GROUP_BY_ENUM);
      }
    },
    [setToolbarGroupBy]
  );

  const {
    data: runSlots,
    isSuccess: isRunModelsSuccess,
    isError: isRunModelsError
  } = useRunSlots(originalCompany, kpi, currentRun?.run_id ?? null);
  const hasNoSlots = isRunModelsSuccess && !runSlots?.length;
  // Slots Scenario Planning can use vs. slots skipped for missing artifacts; memoized so the
  // hroasRun prop below keeps a stable reference across unrelated re-renders.
  const spSlots = useMemo(
    () => (runSlots ?? []).filter(slot => !slot.spMissingArtifacts?.length),
    [runSlots]
  );
  const spSkippedSlots = useMemo(
    () => (runSlots ?? []).filter(slot => slot.spMissingArtifacts?.length),
    [runSlots]
  );

  // WS4 resolution: HROAS is holistic, so there is no single page-level model type — rows from
  // rMMM/GeoMMM/BrandEquity are blended in the same bundle. `featureMappingModelType` is the
  // first *enabled* model's type from the reconciled `modelsData` — the decay-curve fallback
  // resolves against one map, so it must track the run's actual enabled models (e.g. an `away` run
  // with no geo model).
  const featureMappingModelType =
    HROAS_MODEL_ID_TO_FEATURE_MAPPING_TYPE[
      runSlots?.find(slot => slot.slotId === modelsData.find(model => model.enabled)?.id)?.family ??
        ''
    ] ?? 'rMMM';

  // Fetch the feature_id -> pretty-name map for every model type in this run (not just
  // currently-enabled ones), plus featureMappingModelType, and thread the full set down to Model
  // Results, where the adapter layer resolves new-format (client `away`) rows — populated
  // feature_id, blank name/channel — against the map for EACH row's own model_type instead of one
  // representative map. The run's slots map to at most 3 model types, so this is at most 3
  // GETs, deduped/cached by react-query. Some downstream datasets (e.g. the Spend chart's
  // spendUniverse, Efficient Frontier's Channel Mix) are toggle-invariant and always include rows
  // for every model type regardless of what's enabled here, so scoping this to enabled-only models
  // caused those rows to lose their name map (and fall back to raw featids) as soon as a model was
  // toggled off. Keyed on originalCompany (the same cid getHroasResults/listHroasRuns use).
  const activeFeatureMappingTypes = useMemo(() => {
    const allTypes = (runSlots ?? [])
      .map(slot => HROAS_MODEL_ID_TO_FEATURE_MAPPING_TYPE[slot.family])
      .filter((type): type is string => Boolean(type));
    return Array.from(new Set([...allTypes, featureMappingModelType]));
  }, [runSlots, featureMappingModelType]);
  // DEV-24557: tinuititest is a TEST_COMPANIES entry, which would otherwise auto-proxy this
  // lookup to an unrelated Scenario Planning demo company instead of DSW's real taxonomy.
  const featureMapCompany =
    originalCompany === TINUITITEST_CID ? TINUITITEST_FEATURE_MAP_PROXY_CID : originalCompany;
  const { maps: featureMaps } = useFeatureMapsByModelType(
    featureMapCompany,
    activeFeatureMappingTypes
  );
  // Single representative map, kept for the decay-curve path (which only ever resolves against one
  // model type at a time).
  const featureMap = featureMaps[featureMappingModelType];

  const models = useMemo(
    () =>
      modelsData.map(model => {
        const refreshDate = calculateModelRefreshDate(model.id, stackedCSVData);
        const mape = formatMapeAsString(calculateModelMape(model.id, stackedCSVData));
        const metadata: ModelMetadata = {
          runDate: refreshDate,
          mape
        };
        return {
          ...model,
          infoTooltip: createFormattedTooltip(
            model.label,
            model.weight,
            model.id,
            () => setShowEditModal(true),
            metadata,
            tab === TabKey.EFFICIENT_FRONTIER
          )
        };
      }),
    [modelsData, stackedCSVData, tab]
  );
  // Display-only date derived from the selected run's trigger time. Model data
  // now flows to ModelResults through the runId-keyed getHroasResults query;
  // this only feeds the legacy `date` display prop threaded down to CPTB.
  const selectedDate = useMemo(() => {
    if (!currentRun?.triggered_at) {
      return '';
    }
    const parsed = new Date(currentRun.triggered_at);
    return Number.isNaN(parsed.getTime())
      ? ''
      : parsed.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  }, [currentRun]);

  // MmmRunSelector's shape of the runs list; keyed by run_id. Single group: the outcome variable
  // is picked in the toolbar.
  const runKpiRuns = useMemo(
    () => hroasToModelRuns(runOptions, { isInternal, hroasKpi: kpi }),
    [runOptions, isInternal, kpi]
  );

  const location = useRouterLocation();
  const isBudgetOptimizationRoute = useMemo(
    () => location.pathname.includes('/budget-optimizations'),
    [location.pathname]
  );
  // FAQ and Offline Inputs are informational tabs with no model/outcome/run controls,
  // so the whole secondary/control bar is hidden on them (same pattern as budget-opt).
  const isFaqOrOfflineInputsRoute = useMemo(
    () => tab === TabKey.FAQ || tab === TabKey.OFFLINE_INPUTS,
    [tab]
  );

  useEffect(() => {
    const runId = currentRun?.run_id;
    // The model-overview CSV now reads runs/{runId}/ — there is no latest/
    // fallback, so don't fetch until a run resolves. No run (or a client with
    // zero succeeded runs) → clear to the empty state instead of erroring.
    if (!runId) {
      setStackedCSVData([]);
      return;
    }
    const loadStackedCSV = async () => {
      try {
        const data = await fetchModelOverviewRows(originalCompany, kpi, runId);
        setStackedCSVData(data);
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : String(error);
        console.warn('Error fetching model overview from lambda:', errorMessage);
      }
    };

    loadStackedCSV();
  }, [originalCompany, kpi, currentRun?.run_id]);

  useEffect(() => {
    setModelsData([]);
  }, [originalCompany]);

  // Model-toggle list from the run's trained slots (inputs.json models[]), read from the shared
  // model-results bundle (no extra request — same cache key as Model Results). The list is empty
  // until the run's bundle resolves.
  // useLayoutEffect (not useEffect) so the toggle set is in place BEFORE the browser paints; with
  // `modelsReady` below this avoids a flash of an empty or stale toggle list.
  useLayoutEffect(() => {
    if (!isRunModelsSuccess) {
      return;
    }
    const nextModels = slotsToModelConfigs(runSlots ?? []);
    // Only replace when the toggle set's identity (id + label + appLabel) actually
    // changes, so a background bundle refetch never clobbers the user's
    // toggle/weight edits. See modelSetSignature's doc comment for why id alone
    // isn't a sufficient comparison.
    setModelsData(prev =>
      modelSetSignature(prev) === modelSetSignature(nextModels) ? prev : nextModels
    );
  }, [runSlots, isRunModelsSuccess]);

  // Gate for the SecondaryNav toggles: suppressed until the toggle set is final for the current
  // run, so the empty pre-resolution list never renders as "no models".
  const modelsReady = useMemo(() => {
    // Still resolving which run to show — a run may load and replace the set.
    if (loading) return false;
    // No run resolved: nothing will populate the toggle list, so it is final.
    if (!currentRun?.run_id) return true;
    // Run resolved: wait for its model bundle to settle (success or error) so the run's
    // toggle set is in place before the toggles appear.
    return isRunModelsSuccess || isRunModelsError;
  }, [loading, currentRun?.run_id, isRunModelsSuccess, isRunModelsError]);

  useEffect(() => {
    const loadRuns = async () => {
      if (tab === TabKey.OFFLINE_INPUTS || tab === TabKey.FAQ) {
        return;
      }
      try {
        setLoading(true);
        // Keyed on originalCompany (same cid getHroasResults uses) so runs and
        // results resolve to the same client through the _PROXY display remap.
        const runs = await listHroasRuns(originalCompany, kpi);
        // Selectable set: succeeded only (drops legacy submitted + any failed),
        // then external gate — non-internal users only see published runs.
        const gatedRuns = runs
          .filter(run => run.status === 'succeeded')
          .filter(run => isInternal || run.published);
        if (R.isEmpty(gatedRuns)) {
          // No runs for this kpi (e.g. selecting "KPI Volume" for a revenue-only
          // client like poppi — expected, no non_revenue implementation yet).
          // Settle to the no-data state: clear the selected run + options so the
          // stale run can't drive ModelResults into a perpetual FullPageSpinner,
          // the picker hides, and the runId-gated panels stay idle. Not an error.
          setRunOptions([]);
          setCurrentRun(undefined);
          return;
        }
        // Newest published, else newest succeeded. The fallback is mandatory —
        // nothing is published this phase, so without it the page renders blank.
        const initial = pickDefaultRun(gatedRuns);
        setRunOptions(gatedRuns);
        setCurrentRun(initial);

        if (initial) {
          // Prefetch the Model Results bundle for the chosen run so switching
          // to the Model Results tab is instant. This key is IDENTICAL to the one
          // the panels now read — getSpendEffectShareCsvQueryKey / getHolisticRcQueryKey /
          // getModelOverviewLambdaQueryKey / getMarginalDecisionQueryKey all resolve to
          // getHroasResultsQueryKey(company, kpi, "model_results", runId) — so this
          // single fetch genuinely warms the SES + RC + MO + MDD panel cache.
          try {
            await queryClient.prefetchQuery({
              queryKey: getHroasResultsQueryKey(
                originalCompany,
                kpi,
                'model_results',
                initial.run_id
              ),
              queryFn: () =>
                fetchHroasResults(originalCompany, kpi, 'model_results', initial.run_id)
            });
          } catch {
            // Prefetch is best-effort; ignore errors
          }
        }
      } catch (e) {
        // Treat a run-list fetch failure as no-data for the UI (per product: the
        // non_revenue endpoint may reject since it isn't implemented yet). Settle
        // to the same cleared no-data state instead of an error banner + infinite
        // spinner — but log it so genuine failures stay visible in the console.
        const reportError = e as Error;
        console.warn('Error loading HROAS runs:', reportError.message);
        setRunOptions([]);
        setCurrentRun(undefined);
      } finally {
        setLoading(false);
      }
    };
    loadRuns();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- tab omitted: run load should not re-run on tab change per HROAS prefetch plan
  }, [originalCompany, setError, isInternal, queryClient, kpi]);

  const handlePublishToggle = useCallback(async () => {
    if (!currentRun) {
      return;
    }
    const runId = currentRun.run_id;
    const nextPublished = !currentRun.published;
    // Optimistic: flip the run's published flag in local state immediately so the
    // picker section + Client-Facing toggle reflect the change without waiting on
    // the POST. currentRun and the matching runOptions entry are the only reads
    // the picker/HroasPublishToggle rely on.
    setCurrentRun(prev =>
      prev && prev.run_id === runId ? { ...prev, published: nextPublished } : prev
    );
    setRunOptions(prev =>
      prev.map(run => (run.run_id === runId ? { ...run, published: nextPublished } : run))
    );
    try {
      const updated = await setHroasRunPublished(originalCompany, runId, nextPublished);
      // Reconcile quietly with the authoritative returned row, then invalidate the
      // runs query so a later remount is consistent. No success toast — the
      // optimistic UI already reflects the change.
      setCurrentRun(prev => (prev && prev.run_id === updated.run_id ? updated : prev));
      setRunOptions(prev => prev.map(run => (run.run_id === updated.run_id ? updated : run)));
      queryClient.invalidateQueries({ queryKey: getHroasRunsQueryKey(originalCompany, kpi) });
    } catch (e) {
      const reportError = e as Error;
      // Revert the optimistic flip so the UI matches server truth, then surface
      // the failure (default "danger" error variant + the real message).
      setCurrentRun(prev =>
        prev && prev.run_id === runId ? { ...prev, published: !nextPublished } : prev
      );
      setRunOptions(prev =>
        prev.map(run => (run.run_id === runId ? { ...run, published: !nextPublished } : run))
      );
      setError({ message: reportError.message, reportError });
    }
  }, [currentRun, originalCompany, queryClient, setError, kpi]);

  // Prefetch Efficient Frontier with default dates for instant tab switching (internal users only)
  useEffect(() => {
    const efRunId = currentRun?.run_id;
    // EF also reads runs/{runId}/ now — skip the prefetch until a run resolves so
    // we never warm the cache off latest/. Keys/args match the EF hooks exactly.
    if (!isInternal || !efRunId) {
      return;
    }
    const {
      start: efStart,
      end: efEnd,
      planLength: efPlanLength
    } = getEfficientFrontierDefaultDates();
    (async () => {
      try {
        await queryClient.prefetchQuery({
          queryKey: getMarketingOutcomesQueryKey(originalCompany, kpi, efRunId, efStart, efEnd),
          queryFn: () => fetchMarketingOutcomesData(originalCompany, kpi, efRunId, efStart, efEnd)
        });
      } catch {
        // Prefetch is best-effort; ignore errors
      }
      try {
        await queryClient.prefetchQuery({
          queryKey: getChannelMixQueryKey(originalCompany, kpi, efRunId, efPlanLength),
          queryFn: () => fetchChannelMixData(originalCompany, kpi, efRunId, efPlanLength)
        });
      } catch {
        // Prefetch is best-effort; ignore errors
      }
    })();
  }, [originalCompany, isInternal, queryClient, kpi, currentRun?.run_id]);

  const handleRunChange = ({ run: selected }: { run: ModelRun }) => {
    const run = runOptions.find(option => option.run_id === runKey(selected));
    if (run) {
      setCurrentRun(run);
    }
  };

  const renderRunSelector = (menuAlign?: ComponentProps<typeof MmmRunSelector>['menuAlign']) =>
    currentRun && (
      <MmmRunSelector
        kpiRuns={runKpiRuns}
        kpi={kpi}
        selectedDate={currentRun.run_id}
        isInternal={isInternal}
        menuAlign={menuAlign}
        hideKpi
        runLabel='Model Run'
        emptyFallback={<span data-testid='hroas-no-published-runs'>No published runs yet.</span>}
        onChange={handleRunChange}
      />
    );

  // Run picker for the Model Results bar (plus the publish toggle for internal users). The
  // Scenario Planning header renders the same selector below; both share handleRunChange.
  // tinuititest hides this one on Model Results (DEV-24557); Scenario Planning keeps its picker,
  // where switching runs is needed to test HROAS SP.
  const runPicker = currentRun && originalCompany !== TINUITITEST_CID && (
    <div className='hroas-run-controls' data-testid='hroas-run-picker'>
      {renderRunSelector()}
      {isInternal && <HroasPublishToggle currentRun={currentRun} onConfirm={handlePublishToggle} />}
    </div>
  );

  // Scenario Planning header: same control as the single-model SP pages (see ScenarioPlanning.tsx
  // Model Run dropdown) — bare selector, no Model Results bar chrome (padding/border/background).
  const scenarioPlanningRunPicker = renderRunSelector('end');

  // Scenario Planning waits for the run's slots; key={runId} remounts SP on a run switch so
  // scenario/selection state and the baseline cache never carry over between runs.
  const renderScenarioPlanning = (): ReactElement | null => {
    if (loading) {
      return null;
    }
    if (!currentRun) {
      return (
        <div className='hroas-no-data' data-testid='hroas-sp-no-run'>
          No runs available for this outcome variable.
        </div>
      );
    }
    if (hasNoSlots) {
      return (
        <div className='hroas-no-data' data-testid='hroas-sp-no-models'>
          This run has no model information, so there is nothing to show.
        </div>
      );
    }
    if (isRunModelsError) {
      return (
        <div className='hroas-no-data' data-testid='hroas-sp-no-data'>
          Could not load the models for this run. Try again or pick another run.
        </div>
      );
    }
    if (!isRunModelsSuccess) {
      return null;
    }
    if (!spSlots.length) {
      return (
        <div className='hroas-no-data' data-testid='hroas-sp-not-ready'>
          This run has no Scenario Planning-ready models.
          {spSkippedSlots.map(slot => (
            <div key={slot.slotId}>
              {slot.label}: missing {slot.spMissingArtifacts?.join(', ')}
            </div>
          ))}
        </div>
      );
    }
    return (
      <ScenarioPlanning
        key={currentRun.run_id}
        modelType={MODEL_TYPES.HROAS}
        featureMaps={featureMaps}
        hroasRun={{
          cid: originalCompany,
          kpi,
          runId: currentRun.run_id,
          slots: spSlots,
          skippedSlots: spSkippedSlots
        }}
      />
    );
  };
  const scenarioPlanningContent = renderScenarioPlanning();

  const applyWeighting = useCallback((updatedModels: ModelConfig[]) => {
    setModelsData(updatedModels.map(({ infoTooltip, ...rest }) => rest));
  }, []);

  return (
    <Page
      actions={tab === TabKey.SCENARIO_PLANNING ? scenarioPlanningRunPicker : undefined}
      navs={NAVS}
      onNav={handleNav}
      pageType='HROAS'
      selectedNav={optimisticTab}
      title='Holistic ROAS'
      subHeader={
        tab !== TabKey.SCENARIO_PLANNING &&
        !isBudgetOptimizationRoute &&
        !isFaqOrOfflineInputsRoute && (
          <>
            <SecondaryNav
              models={models}
              modelsReady={modelsReady}
              onModelsChange={updatedModels => {
                // Defer state update so the toggle can paint before heavy ModelResults work runs
                setTimeout(() => {
                  setModelsData(updatedModels.map(({ infoTooltip, ...rest }) => rest));
                }, 0);
              }}
              onEditModel={() => setShowEditModal(true)}
            />
            {pageActions && <div className='secondary-nav-page-actions'>{pageActions}</div>}
            {tab !== TabKey.EFFICIENT_FRONTIER && (
              <div className='hroas-model-results-bar'>
                {/* Left: Outcome Variable + Group by (from the shared toolbar) */}
                {toolbar}
                {/* Right: run picker + Client-Facing publish toggle. Hidden for tinuititest (DEV-24557). */}
                {runPicker}
              </div>
            )}
          </>
        )
      }
    >
      <EditModelWeightingModal
        show={showEditModal}
        onHide={() => setShowEditModal(false)}
        models={models}
        onSave={applyWeighting}
        currentUserName={currentUserName}
      />
      <div
        className='full-page-router mmm-page'
        data-testid='hroas-efficient-frontier'
        style={{ display: tab === TabKey.EFFICIENT_FRONTIER ? 'flex' : 'none' }}
      >
        <EfficientFrontier
          active={tab === TabKey.EFFICIENT_FRONTIER}
          models={models}
          slots={runSlots ?? []}
          kpi={kpi}
          runId={currentRun?.run_id ?? null}
          featureMaps={featureMaps}
          featureMap={featureMap}
        />
      </div>
      {!loading && currentRun && hasNoSlots && (
        <div
          className='full-page-router mmm-page hroas-no-data'
          data-testid='hroas-no-models'
          style={{ display: tab === TabKey.MODEL_RESULTS ? 'flex' : 'none' }}
        >
          This run has no model information, so there is nothing to show.
        </div>
      )}
      {!loading && currentRun && !hasNoSlots && (
        <div
          className='full-page-router mmm-page'
          data-testid='hroas-model-results'
          style={{ display: tab === TabKey.MODEL_RESULTS ? 'block' : 'none' }}
        >
          <ModelResults
            showActualizedData={showActualizedData}
            disabledPlatform={disabledPlatform}
            groupBy={groupBy}
            setGroupBy={setGroupBy}
            groupByOptions={groupByOptions}
            setGroupByOptions={setGroupByOptions}
            kpi={kpi}
            runId={currentRun.run_id}
            date={selectedDate}
            models={models}
            onModelsChange={applyWeighting}
            toolbarOutcomeVariable={toolbarOutcomeVariable}
            featureMaps={featureMaps}
            featureMap={featureMap}
          />
        </div>
      )}
      {/* Settled with no run for the selected Outcome Variable (empty list or a
          rejected run-list fetch). Render a stable no-data panel in the Model
          Results slot instead of a perpetual spinner or a shifted layout. */}
      {!loading && !currentRun && (
        <div
          className='full-page-router mmm-page hroas-no-data'
          data-testid='hroas-no-data'
          style={{ display: tab === TabKey.MODEL_RESULTS ? 'flex' : 'none' }}
        >
          No runs available for this outcome variable.
        </div>
      )}
      <div className='full-page-router mmm-page'>
        <Routes>
          <Route path={TabKey.FAQ} element={<FAQPage hroas showTechnicalDocumentation={false} />} />
          <Route path={`${TabKey.BUDGET_OPTIMIZATION}/*`} element={<Optimizations />} />
          <Route path={TabKey.OFFLINE_INPUTS} element={<OfflineInputs />} />
          <Route path={TabKey.SCENARIO_PLANNING} element={scenarioPlanningContent} />
          <Route
            path='offlineinputs'
            element={<Navigate to={`../${TabKey.OFFLINE_INPUTS}`} replace />}
          />
          <Route
            path='offline_inputs'
            element={<Navigate to={`../${TabKey.OFFLINE_INPUTS}`} replace />}
          />
        </Routes>
      </div>
    </Page>
  );
};
